import {Clipboard} from '../services/clipboard.ts';
import {extractRtfImages, htmlHasLoadableImage, markUnloadableImages} from './clipboard-rtf.ts';

let wrap;
let draggedContentEditable;

function hasTextContent(node) {
    return node && !!(node.textContent || node.innerText);
}

/**
 * Upload an image file to the server
 * @param {File} file
 * @param {int} pageId
 * @param {?string} fileName
 */
async function uploadImageFile(file, pageId, fileName = null) {
    const remoteFilename = fileName || file.name || `image-${Date.now()}.png`;

    if (file === null || (!file.type.includes('image') && !Clipboard.hasImageExtension(remoteFilename))) {
        throw new Error('Not an image file');
    }

    const formData = new FormData();
    formData.append('file', file, remoteFilename);
    formData.append('uploaded_to', pageId);

    const resp = await window.$http.post(window.baseUrl('/images/gallery'), formData);
    return resp.data;
}

/**
 * Build the TinyMCE native images_upload_handler (v21.04-style) used for
 * paste & drag-drop image uploads. Resolves to the uploaded full image URL
 * so TinyMCE inserts a plain full-resolution <img> (no thumbnail/link wrapper).
 * @param {WysiwygConfigOptions} options
 * @return {function(Object): Promise<string>}
 */
export function buildImageUploadHandler(options) {
    return blobInfo => new Promise((resolve, reject) => {
        uploadImageFile(blobInfo.blob(), options.pageId, blobInfo.filename())
            .then(resp => resolve(resp.url))
            .catch(err => {
                window.$events.error(err?.data?.message || options.translations.imageUploadErrorText);
                console.error(err);
                reject({message: options.translations.imageUploadErrorText, remove: true});
            });
    });
}

/**
 * Handle pasting images from clipboard.
 * @param {Editor} editor
 * @param {WysiwygConfigOptions} options
 * @param {ClipboardEvent|DragEvent} event
 */
function paste(editor, options, event) {
    const clipboard = new Clipboard(event.clipboardData || event.dataTransfer);

    // Don't handle the event ourselves if no items exist of contains table-looking data
    if (!clipboard.hasItems() || clipboard.containsTabularData()) {
        return;
    }

    const images = clipboard.getImages();
    for (const imageFile of images) {
        const id = `image-${Math.random().toString(16).slice(2)}`;
        const loadingImage = window.baseUrl('/loading.gif');
        event.preventDefault();

        setTimeout(() => {
            editor.insertContent(`<p><img src="${loadingImage}" id="${id}"></p>`);

            uploadImageFile(imageFile, options.pageId).then(resp => {
                const safeName = resp.name.replace(/"/g, '');
                const newImageHtml = `<img src="${resp.thumbs.display}" alt="${safeName}" />`;

                const newEl = editor.dom.create('a', {
                    target: '_blank',
                    href: resp.url,
                }, newImageHtml);

                editor.dom.replace(newEl, id);
            }).catch(err => {
                editor.dom.remove(id);
                window.$events.error(err?.data?.message || options.translations.imageUploadErrorText);
                console.error(err);
            });
        }, 10);
    }
}

/**
 * Safely read a clipboard flavour — Safari can throw for non-text types during a paste event.
 * @param {DataTransfer} dataTransfer
 * @param {string} type
 * @return {string}
 */
function readClipboardData(dataTransfer, type) {
    try {
        return dataTransfer.getData(type) || '';
    } catch {
        return '';
    }
}

// Images recovered from the most recent paste's text/rtf flavour, in document order. Set by the paste
// listener, then consumed by fillPastedImages once the placeholders that paste_preprocess leaves have
// landed in the editor body.
let pendingRtfImages = [];

/**
 * Read and remember the images embedded in a paste's text/rtf flavour so fillPastedImages can later
 * drop them into the in-position placeholders. Clears any previous set; safe to call for every paste
 * (leaves an empty set when there's nothing recoverable).
 * @param {DataTransfer} dataTransfer
 */
function captureRtfPasteImages(dataTransfer) {
    pendingRtfImages = [];

    const rtf = readClipboardData(dataTransfer, 'text/rtf');
    if (!rtf) {
        return;
    }

    // If the HTML already carries loadable (data:/http) images, native paste inserts those in place —
    // don't also pull them from the RTF.
    if (htmlHasLoadableImage(readClipboardData(dataTransfer, 'text/html'))) {
        return;
    }

    try {
        pendingRtfImages = extractRtfImages(rtf);
    } catch (err) {
        console.error('Failed to parse images from pasted RTF', err);
        pendingRtfImages = [];
    }
}

/**
 * The placeholder src used to mark a pasted image position and carry its document-order index so
 * fillPastedImages can pair it with the matching RTF image.
 * @param {int} index
 * @return {string}
 */
function placeholderSrc(index) {
    return window.baseUrl(`/loading.gif?rtfpaste=${index}`);
}

/**
 * Replace each pasted image the browser can't load (macOS file:// / webkit-fake-url:) with an
 * in-position loading placeholder carrying its document-order index — keeping the surrounding text and
 * formatting. Called from paste_preprocess; fillPastedImages later swaps each placeholder for the
 * matching recovered image (or removes it when there's no match).
 * @param {string} html
 * @return {string}
 */
export function markPastedImagePlaceholders(html) {
    return markUnloadableImages(html, placeholderSrc);
}

/**
 * After a paste has been inserted, swap each placeholder (see markPastedImagePlaceholders) for its
 * matching recovered RTF image IN PLACE — raster to the gallery, EMF/WMF via the LibreOffice sidecar —
 * so images land exactly where they sat in the pasted content. Placeholders with no matching image are
 * removed.
 * @param {Editor} editor
 * @param {WysiwygConfigOptions} options
 */
function fillPastedImages(editor, options) {
    const placeholders = editor.dom.select('img[src*="rtfpaste="]');
    const images = pendingRtfImages;
    pendingRtfImages = [];

    console.warn('[word-paste] filling', placeholders.length, 'placeholder(s) from', images.length, 'RTF image(s)');

    for (const placeholder of placeholders) {
        const match = (placeholder.getAttribute('src') || '').match(/rtfpaste=(\d+)/);
        const image = match ? images[parseInt(match[1], 10)] : null;

        if (!image) {
            editor.dom.remove(placeholder);
            continue;
        }

        let file;
        try {
            const name = `pasted-image-${Math.random().toString(16).slice(2)}.${image.extension}`;
            file = new File([image.bytes], name, {type: image.contentType});
        } catch (err) {
            console.error('Failed to build file from pasted RTF image', err);
            editor.dom.remove(placeholder);
            continue;
        }

        // Clear the marker so a slow upload can't be re-matched by a later fill pass.
        placeholder.setAttribute('src', window.baseUrl('/loading.gif'));

        // Raster uploads straight to the gallery; EMF/WMF go through the LibreOffice sidecar (the same
        // Word-import endpoint), which returns a stored PNG. Both respond with the same image JSON.
        const endpoint = image.needsConversion ? '/images/word-import' : '/images/gallery';
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('uploaded_to', options.pageId);

        window.$http.post(window.baseUrl(endpoint), formData).then(({data: resp}) => {
            const safeName = (resp.name || '').replace(/"/g, '');
            const displaySrc = resp.thumbs?.display || resp.url;
            const anchor = editor.dom.create('a', {
                target: '_blank',
                href: resp.url,
            }, `<img src="${displaySrc}" alt="${safeName}" />`);

            editor.dom.replace(anchor, placeholder);
        }).catch(err => {
            editor.dom.remove(placeholder);
            window.$events.error(err?.data?.message || options.translations.imageUploadErrorText);
            console.error(err);
        });
    }
}

/**
 * @param {Editor} editor
 */
function dragStart(editor) {
    const node = editor.selection.getNode();

    if (node.nodeName === 'IMG') {
        wrap = editor.dom.getParent(node, '.mceTemp');

        if (!wrap && node.parentNode.nodeName === 'A' && !hasTextContent(node.parentNode)) {
            wrap = node.parentNode;
        }
    }

    // Track dragged contenteditable blocks
    if (node.hasAttribute('contenteditable') && node.getAttribute('contenteditable') === 'false') {
        draggedContentEditable = node;
    }
}

/**
 * @param {Editor} editor
 * @param {WysiwygConfigOptions} options
 * @param {DragEvent} event
 */
function drop(editor, options, event) {
    const {dom} = editor;
    const rng = window.tinymce.dom.RangeUtils.getCaretRangeFromPoint(
        event.clientX,
        event.clientY,
        editor.getDoc(),
    );

    // Template insertion
    const templateId = event.dataTransfer && event.dataTransfer.getData('bookstack/template');
    if (templateId) {
        event.preventDefault();
        window.$http.get(`/templates/${templateId}`).then(resp => {
            editor.selection.setRng(rng);
            editor.undoManager.transact(() => {
                editor.execCommand('mceInsertContent', false, resp.data.html);
            });
        });
    }

    // Don't allow anything to be dropped in a captioned image.
    if (dom.getParent(rng.startContainer, '.mceTemp')) {
        event.preventDefault();
    } else if (wrap) {
        event.preventDefault();

        editor.undoManager.transact(() => {
            editor.selection.setRng(rng);
            editor.selection.setNode(wrap);
            dom.remove(wrap);
        });
    }

    // Handle contenteditable section drop
    if (!event.isDefaultPrevented() && draggedContentEditable) {
        event.preventDefault();
        editor.undoManager.transact(() => {
            const selectedNode = editor.selection.getNode();
            const range = editor.selection.getRng();
            const selectedNodeRoot = selectedNode.closest('body > *');
            if (range.startOffset > (range.startContainer.length / 2)) {
                selectedNodeRoot.after(draggedContentEditable);
            } else {
                selectedNodeRoot.before(draggedContentEditable);
            }
        });
    }

    // Handle image insert
    if (!event.isDefaultPrevented()) {
        paste(editor, options, event);
    }

    wrap = null;
}

/**
 * @param {Editor} editor
 * @param {DragEvent} event
 */
function dragOver(editor, event) {
    // This custom handling essentially emulates the default TinyMCE behaviour while allowing us
    // to specifically call preventDefault on the event to allow the drop of custom elements.
    event.preventDefault();
    editor.focus();
    const rangeUtils = window.tinymce.dom.RangeUtils;
    const range = rangeUtils.getCaretRangeFromPoint(event.clientX ?? 0, event.clientY ?? 0, editor.getDoc());
    editor.selection.setRng(range);
}

/**
 * @param {Editor} editor
 * @param {WysiwygConfigOptions} options
 */
export function listenForDragAndPaste(editor, options) {
    editor.on('dragover', event => dragOver(editor, event));
    editor.on('dragstart', () => dragStart(editor));
    editor.on('drop', event => drop(editor, options, event));
    editor.on('paste', event => paste(editor, options, event));
}

/**
 * Additive paste hook for macOS rich paste (Apple Pages / Word): recover images embedded in the
 * text/rtf clipboard flavour (which TinyMCE's native paste ignores) and upload them, WITHOUT taking
 * over the paste — native handling still inserts the text/html formatting and any image files. Skips
 * tabular pastes and pastes that already carry image files (native handles those).
 * @param {Editor} editor
 * @param {WysiwygConfigOptions} options
 */
export function listenForRtfImagePaste(editor, options) {
    editor.on('paste', event => {
        const dataTransfer = event.clipboardData || event.dataTransfer;
        if (!dataTransfer) {
            return;
        }

        // Capture the RTF images now (clipboard is only readable during the event). paste_preprocess
        // runs afterwards and turns each unloadable <img> into an in-position placeholder; defer the
        // fill until those placeholders have been inserted into the body.
        captureRtfPasteImages(dataTransfer);

        console.warn('[word-paste] types:', Array.from(dataTransfer.types || []),
            '| recovered', pendingRtfImages.length, 'RTF image(s)');

        setTimeout(() => fillPastedImages(editor, options), 50);
    });
}
